import { AlertCircle, RefreshCw, Unplug } from "lucide-react";
import { useCallback, useState } from "react";

import { Button } from "@/common/ui/button";

import { ConnectDeviceCard } from "./ConnectDeviceCard";
import { DisconnectDialog } from "./DisconnectDialog";
import { useDeviceConnections } from "./use-device-connections";
import { useDeviceData } from "./use-device-data";
import { WhoopDataDashboard } from "./whoop-data";

export function DevicesPage(): React.ReactElement {
  const { connections, isLoading, error, connect, disconnect, refresh } = useDeviceConnections();
  const [disconnectTarget, setDisconnectTarget] = useState<string | null>(null);

  const whoopConnection = connections.find((c) => c.provider === "whoop");
  const isConnected = whoopConnection !== undefined;

  const {
    data,
    isLoading: dataLoading,
    error: dataError,
    refresh: refreshData,
  } = useDeviceData(isConnected);

  const handleConnect = useCallback(
    (provider: string) => {
      void connect(provider);
    },
    [connect]
  );

  const handleDisconnectConfirm = useCallback(async () => {
    if (!disconnectTarget) return;
    await disconnect(disconnectTarget);
    setDisconnectTarget(null);
  }, [disconnectTarget, disconnect]);

  const handleRefresh = useCallback(() => {
    refresh();
    refreshData();
  }, [refresh, refreshData]);

  const displayError = error ?? dataError;

  return (
    <div data-testid="devices-page" className="space-y-6">
      {isConnected && (
        <div className="flex items-center justify-end gap-2">
          <Button
            data-testid="refresh-data-button"
            variant="outline"
            size="sm"
            onClick={handleRefresh}
          >
            <RefreshCw className="mr-2 h-4 w-4" />
            Refresh
          </Button>
          <Button
            data-testid="disconnect-button"
            variant="outline"
            size="sm"
            onClick={() => setDisconnectTarget("whoop")}
          >
            <Unplug className="mr-2 h-4 w-4" />
            Disconnect
          </Button>
        </div>
      )}

      {displayError && (
        <div
          data-testid="devices-error"
          className="flex items-center gap-2 rounded-lg border border-destructive/50 bg-destructive/10 p-4 text-sm text-destructive"
        >
          <AlertCircle className="h-4 w-4 shrink-0" />
          <span>{displayError}</span>
          <Button variant="outline" size="sm" className="ml-auto" onClick={handleRefresh}>
            Retry
          </Button>
        </div>
      )}

      {!isLoading && !isConnected && (
        <ConnectDeviceCard onConnect={handleConnect} isLoading={isLoading} />
      )}

      {isConnected && <WhoopDataDashboard data={data} isLoading={dataLoading} />}

      <DisconnectDialog
        provider={disconnectTarget}
        open={disconnectTarget !== null}
        onConfirm={() => void handleDisconnectConfirm()}
        onCancel={() => setDisconnectTarget(null)}
      />
    </div>
  );
}
